import React, {
    useImperativeHandle,
    useRef,
    useEffect,
    forwardRef,
    useState,
} from "react";
import { Modal } from "bootstrap";
import { BACKOFFICE_BASE_URL } from "../../services/TapApiUrls";
import HttpAPICall from "../../services/HttpAPICall";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../components/ui/Loader/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const ModuleUpdatePopup = forwardRef(({ onUpdateSuccess }, ref) => {
    const access_token = useSelector((state) => state.auth.access_token);
    const modalElem = useRef(null);
    const searchModal = useRef(null);
    const [formLoading, setFormLoading] = useState(false);
    const MODULE_COUNT_FIELDS = {
        iam: [
            { key: "total_assets", label: "Assets" },
            { key: "total_users", label: "Users" },
            { key: "total_sites", label: "Sites" },
        ],
        inventory: [
            { key: "total_warehouses", label: "Warehouses" },
            { key: "total_items", label: "Items" },
        ],
        hrm: [
            { key: "total_employees", label: "Employees" },
        ],
        crm_deal: [
            { key: "total_deals", label: "Deals" },
        ],
    };

    const MODULE_KEY_MAP = {
        iam: "iam",
        isc: "inventory",
        hrm: "hrm",
        scrm: "crm_deal",
    };
    const [formData, setFormData] = useState({
        module_name: "",
        module_key: null,
        module_id: null,
        tap_master_id: null,
        from_date: null,
        till_date: null,
    });

    const [counts, setCounts] = useState({});
    const [originalCounts, setOriginalCounts] = useState({});

    useImperativeHandle(ref, () => ({
        openEditModal: (moduleData) => {
            if (moduleData && typeof moduleData === "object") {
                const normalizedKey =
                    MODULE_KEY_MAP[moduleData.module_key] || moduleData.module_key || null;

                const initCounts = moduleData.counts || {};

                setFormData({
                    module_name: moduleData.name || "",
                    module_key: normalizedKey,
                    module_id: moduleData.id || null,
                    tap_master_id: moduleData.tap_master_id || null,
                    from_date: moduleData.from ? moment(moduleData.from).toDate() : new Date(),
                    till_date: moduleData.till ? moment(moduleData.till).toDate() : null,
                });
                setCounts(initCounts);
                setOriginalCounts(initCounts);
                searchModal.current.show();
            } else {
                toast.error("Invalid data format", { position: toast.POSITION.TOP_RIGHT });
            }
        },
        closeModal: () => {
            searchModalClose();
        },
    }));
    const getModuleUpdateEndpoint = (moduleKey) => {
        switch (moduleKey) {
            case 'iam': return '/group/update_iam_count';
            case 'inventory': return '/group/update_isc_count';
            case 'hrm': return '/group/update_hrm_count';
            case 'crm_deal': return '/group/update_scrm_count';
            default: return '/group/update_count';
        }
    };
    useEffect(() => {
        modalElem.current = document.getElementById("moduleEditModalID");
        searchModal.current = new Modal(modalElem.current, {
            keyboard: false,
            backdrop: "static",
        });

        const handleHidden = () => {
            setFormLoading(false);
        };

        modalElem.current.addEventListener("hidden.bs.modal", handleHidden);

        return () => {
            if (searchModal.current) searchModal.current.dispose();
            if (modalElem.current) {
                modalElem.current.removeEventListener("hidden.bs.modal", handleHidden);
            }
        };
    }, []);

    const searchModalClose = () => {
        if (searchModal.current) searchModal.current.hide();
        setFormLoading(false);
    };

    const handleDateChange = (date, fieldName) => {
        setFormData((prev) => ({ ...prev, [fieldName]: date }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const fromDate = formData.from_date || new Date();

        if (!formData.till_date) {
            toast.error("Till date is required", { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (new Date(formData.till_date) <= new Date(fromDate)) {
            toast.error("Till date must be after From date", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        setFormLoading(true);

        const payload = {
            tap_master_id: formData.tap_master_id,
            module_id: formData.module_id,
            form_date: moment(fromDate).format("YYYY-MM-DD"),
            till_date: moment(formData.till_date).format("YYYY-MM-DD"),
        };

        // counts add karo
        Object.keys(counts).forEach((k) => {
            payload[k] = Number(counts[k]) || 0;
        });

        const endpoint = getModuleUpdateEndpoint(formData.module_key);

        HttpAPICall.withAthorization(
            "PUT",
            BACKOFFICE_BASE_URL + endpoint,
            access_token,
            {},
            payload,
            (resp) => {
                toast.success("Module updated successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                if (onUpdateSuccess) onUpdateSuccess();
                searchModalClose();
            },
            (error) => {
                toast.error(error?.message || "Failed to update module", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        ).finally(() => setFormLoading(false));
    };
    return (
        <div className="modal fade" id="moduleEditModalID" tabIndex="-1" ref={modalElem}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Module</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={searchModalClose}
                            aria-label="Close"
                            disabled={formLoading}
                        />
                    </div>

                    <div className="modal-body">
                        {formLoading ? (
                            <div className="text-center py-5">
                                <Loader />
                            </div>
                        ) : (
                            <form id="moduleEditForm" onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="row align-items-center py-2 justify-content-center">
                                        <label className="col-sm-3 form-label require">Module Name</label>
                                        <div className="col-sm-8">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.module_name}
                                                disabled
                                                style={{ backgroundColor: "#e9ecef" }}
                                            />
                                        </div>
                                    </div>

                                    <div className="row align-items-center py-2 justify-content-center">
                                        <label className="col-sm-3 form-label require">From Date</label>
                                        <div className="col-sm-8">
                                            <DatePicker
                                                selected={formData.from_date}
                                                onChange={(date) => handleDateChange(date, "from_date")}
                                                dateFormat="dd-MMM-yyyy"
                                                className="form-control"
                                                showMonthDropdown
                                                showYearDropdown
                                                autoComplete="off"
                                                scrollMonthYearDropdown
                                                placeholderText="Select From Date"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="row align-items-center py-2 justify-content-center">
                                        <label className="col-sm-3 form-label require">Till Date</label>
                                        <div className="col-sm-8">
                                            <DatePicker
                                                selected={formData.till_date}
                                                onChange={(date) => handleDateChange(date, "till_date")}
                                                dateFormat="dd-MMM-yyyy"
                                                className="form-control"
                                                showMonthDropdown
                                                showYearDropdown
                                                autoComplete="off"
                                                scrollMonthYearDropdown
                                                placeholderText="Select Till Date"
                                                minDate={formData.from_date || null}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={searchModalClose}
                            disabled={formLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="moduleEditForm"
                            className="btn btn-primary"
                            disabled={formLoading}
                        >
                            {formLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Updating...
                                </>
                            ) : (
                                "Update Module"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default ModuleUpdatePopup;